import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../api/api';
import authService from '../authService';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing token and user
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    }
    
    setLoading(false);
  }, []);

  // Login function - prepared for Auth0 integration
  const login = async (email, password) => {
    try {
      // TODO: Replace with Auth0 authentication
      // For now, this will throw an error indicating Auth0 is required
      const authData = await authService.login(email, password);
      
      // This code will be used when Auth0 is implemented
      setUser(authData.user);
      setToken(authData.access_token);
      
      // Update API default headers with the new token
      api.defaults.headers.common['Authorization'] = `Bearer ${authData.access_token}`;
      
      // Store in localStorage
      localStorage.setItem('token', authData.access_token);
      localStorage.setItem('user', JSON.stringify(authData.user));
      
      console.log('🔐 Token stored and API headers updated');
      console.log('Token preview:', authData.access_token.substring(0, 20) + '...');
      
      return authData;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Logout function
  const logout = () => {
    authService.logout();
    setUser(null);
    setToken(null);
    
    // Clear API headers
    api.defaults.headers.common['Authorization'] = '';
    
    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    console.log('🔓 Logged out - token and headers cleared');
  };

  // Check if user is authenticated
  const isAuthenticated = !!user && !!token;

  // Manual state update functions for direct API calls
  const updateUser = (userData) => {
    setUser(userData);
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
    } else {
      localStorage.removeItem('user');
    }
  };

  const updateToken = (tokenData) => {
    setToken(tokenData);
    if (tokenData) {
      localStorage.setItem('token', tokenData);
      api.defaults.headers.common['Authorization'] = `Bearer ${tokenData}`;
    } else {
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
    }
  };

  // Auth0 integration helper functions
  const handleAuth0Login = (authData) => {
    if (authData.user && authData.access_token) {
      setUser(authData.user);
      setToken(authData.access_token);
      
      // Update API headers
      api.defaults.headers.common['Authorization'] = `Bearer ${authData.access_token}`;
      
      // Store in localStorage
      localStorage.setItem('token', authData.access_token);
      localStorage.setItem('user', JSON.stringify(authData.user));
      
      console.log('🔐 Auth0 login successful - token stored and API headers updated');
      return authData;
    }
    throw new Error('Invalid Auth0 response data');
  };

  const value = {
    user,
    token,
    login,
    logout,
    loading,
    isAuthenticated,
    updateUser,
    updateToken,
    handleAuth0Login, // New function for Auth0 integration
    // Helper functions
    getUserRole: () => user?.role,
    getRedirectUrl: () => authService.getRedirectUrl()
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};